/**
 * Spending this week — a column of daily spend against the household's daily budget.
 *
 * Hand-rolled inline SVG on purpose: this repo doesn't take a charting dependency
 * (same reasoning as the Mapbox note in the location module). The geometry uses
 * percentage x-coordinates against a fixed-pixel height, so the chart is fully
 * responsive without a viewBox — text stays at real pixel sizes and never shrinks
 * to illegibility on a phone.
 *
 * Color follows the money page's own language: a day at or under the daily budget
 * reads calm (`ok`, sage); a day over it reads `over` (clay), the same tokens the
 * category cards use. Over/under is never carried by color alone — every over day
 * sits above the labeled threshold line, is called out in the legend, and is spelled
 * out in the visually-hidden table. Today's column is highlighted so "where are we
 * right now" is answerable at a glance.
 */
import { useId } from 'react';
import { money } from '../../lib/api';
import type { DaySpend } from './week';

// --- fixed geometry (px on the y axis, % on the x axis) ---------------------
const PAD_TOP = 24; // headroom for value labels above the tallest bar
const PLOT_H = 190; // the bar area
const PAD_BOTTOM = 46; // weekday + date, two lines
const H = PAD_TOP + PLOT_H + PAD_BOTTOM;
const BASELINE_Y = PAD_TOP + PLOT_H;
const PLOT_LEFT = 12; // %, left gutter for the y-axis tick labels
const PLOT_RIGHT = 99; // %
const PLOT_W = PLOT_RIGHT - PLOT_LEFT;
const BAND = PLOT_W / 7;
const BAR_W = BAND * 0.5; // half the band is air — the mark stays quiet
const CORNER = 4; // rounded data-end (skill spec)

/** Round up to a clean axis maximum: 1, 2, 2.5, or 5 × a power of ten. */
function niceCeil(n: number): number {
  if (n <= 0) return 0;
  const pow = Math.pow(10, Math.floor(Math.log10(n)));
  const frac = n / pow;
  const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return nice * pow;
}

function bandCenter(i: number): number {
  return PLOT_LEFT + (i + 0.5) * BAND;
}

function dayParts(iso: string): { weekday: string; date: string } {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return {
    weekday: dt.toLocaleDateString('en-US', { weekday: 'short' }),
    date: dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  };
}

export function WeekSpendChart({
  days,
  dailyBudget,
  today,
}: {
  days: DaySpend[];
  dailyBudget: number | null;
  today: string;
}) {
  const clipId = useId();

  const maxSpend = days.reduce((m, d) => Math.max(m, d.total), 0);
  const totalWeek = days.reduce((sum, d) => sum + d.total, 0);
  const overCount = dailyBudget != null ? days.filter((d) => d.total > dailyBudget).length : 0;
  const todayIdx = days.findIndex((d) => d.date === today);
  const maxIdx = days.reduce((best, d, i) => (d.total > days[best].total ? i : best), 0);

  // Scale to whichever is taller — the biggest day or the budget line — so the
  // threshold is always on-canvas and no bar is clipped. 2% headroom keeps a bar
  // that exactly hits a round number from gluing to the top edge.
  const rawMax = Math.max(maxSpend, dailyBudget ?? 0);
  const niceMax = rawMax > 0 ? niceCeil(rawMax * 1.02) : 5000;
  const yOf = (cents: number) => BASELINE_Y - (Math.min(cents, niceMax) / niceMax) * PLOT_H;

  const ticks = [0, niceMax / 2, niceMax];
  const thresholdY = dailyBudget != null ? yOf(dailyBudget) : null;

  // Label selectively: today and any over-budget day. Nothing on every bar.
  const labelled = new Set<number>();
  if (todayIdx >= 0) labelled.add(todayIdx);
  days.forEach((d, i) => {
    if (dailyBudget != null && d.total > dailyBudget) labelled.add(i);
  });

  const first = days[0] ? dayParts(days[0].date) : null;
  const last = days[days.length - 1] ? dayParts(days[days.length - 1].date) : null;
  const summary =
    `Spending this week` +
    (first && last ? `, ${first.weekday} ${first.date} to ${last.weekday} ${last.date}.` : '.') +
    ` Total spent ${money(totalWeek)}.` +
    (dailyBudget != null
      ? ` ${overCount} of ${days.length} ${overCount === 1 ? 'day' : 'days'} over the ${money(dailyBudget)} daily budget.`
      : ' No daily budget set.') +
    (maxSpend > 0 ? ` Highest day ${dayParts(days[maxIdx].date).weekday}, ${money(maxSpend)}.` : '');

  return (
    <figure className="m-0">
      {/* Legend — the identity channel, so over/under never rests on color alone. */}
      <figcaption className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-soft">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-ok" />
          On track
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-over" />
          Over daily budget
        </span>
        {dailyBudget != null && (
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-0 w-4 border-t-2 border-dashed border-ink-soft"
            />
            Daily budget {money(dailyBudget)}
          </span>
        )}
      </figcaption>

      <svg
        role="img"
        aria-label={summary}
        width="100%"
        height={H}
        className="block overflow-visible"
      >
        {/* Only the top of a bar is rounded; the clip cuts the bottom square on the baseline. */}
        <defs>
          <clipPath id={clipId}>
            <rect x={`${PLOT_LEFT}%`} y={0} width={`${PLOT_W}%`} height={BASELINE_Y} />
          </clipPath>
        </defs>

        {/* Gridlines + y-axis ticks (recessive). */}
        {ticks.map((t) => {
          const y = yOf(t);
          return (
            <g key={`tick-${t}`}>
              <line
                x1={`${PLOT_LEFT}%`}
                x2={`${PLOT_RIGHT}%`}
                y1={y}
                y2={y}
                className="stroke-border"
                strokeWidth={1}
              />
              <text
                x={`${PLOT_LEFT - 1}%`}
                y={y + 3}
                textAnchor="end"
                className="fill-ink-faint tabular-nums"
                fontSize={10}
              >
                {money(t)}
              </text>
            </g>
          );
        })}

        {/* Today's column highlight — "where are we right now". */}
        {todayIdx >= 0 && (
          <rect
            x={`${PLOT_LEFT + todayIdx * BAND}%`}
            y={PAD_TOP - 6}
            width={`${BAND}%`}
            height={PLOT_H + 6}
            className="fill-surface-sunk"
            rx={6}
          />
        )}

        {/* Bars. */}
        <g clipPath={`url(#${clipId})`}>
          {days.map((d, i) => {
            if (d.total <= 0) return null;
            const over = dailyBudget != null && d.total > dailyBudget;
            const top = yOf(d.total);
            const x = bandCenter(i) - BAR_W / 2;
            return (
              <rect
                key={`bar-${d.date}`}
                x={`${x}%`}
                y={top}
                width={`${BAR_W}%`}
                height={BASELINE_Y - top + CORNER}
                rx={CORNER}
                className={over ? 'fill-over' : 'fill-ok'}
              >
                <title>
                  {dayParts(d.date).weekday} {dayParts(d.date).date}: {money(d.total)}
                  {over ? ' — over daily budget' : ''}
                </title>
              </rect>
            );
          })}
        </g>

        {/* Baseline. */}
        <line
          x1={`${PLOT_LEFT}%`}
          x2={`${PLOT_RIGHT}%`}
          y1={BASELINE_Y}
          y2={BASELINE_Y}
          className="stroke-border"
          strokeWidth={1}
        />

        {/* The threshold: daily budget. Dashed to read as a target, not a data mark. */}
        {thresholdY != null && dailyBudget != null && (
          <g>
            <line
              x1={`${PLOT_LEFT}%`}
              x2={`${PLOT_RIGHT}%`}
              y1={thresholdY}
              y2={thresholdY}
              className="stroke-ink-soft"
              strokeWidth={2}
              strokeDasharray="5 4"
            />
            <text
              x={`${PLOT_RIGHT}%`}
              y={thresholdY < 18 ? thresholdY + 14 : thresholdY - 6}
              textAnchor="end"
              className="fill-ink-soft tabular-nums"
              fontSize={10}
            >
              Daily budget {money(dailyBudget)}
            </text>
          </g>
        )}

        {/* Selective value labels — today and over-budget days only. */}
        {days.map((d, i) => {
          if (!labelled.has(i) || d.total <= 0) return null;
          const labelY = Math.max(yOf(d.total) - 6, 12);
          return (
            <text
              key={`val-${d.date}`}
              x={`${bandCenter(i)}%`}
              y={labelY}
              textAnchor="middle"
              className={i === todayIdx ? 'fill-ink font-semibold tabular-nums' : 'fill-ink tabular-nums'}
              fontSize={11}
            >
              {money(d.total)}
            </text>
          );
        })}

        {/* X-axis: weekday over date. Today is emphasized. */}
        {days.map((d, i) => {
          const p = dayParts(d.date);
          const isToday = i === todayIdx;
          return (
            <g key={`x-${d.date}`}>
              <text
                x={`${bandCenter(i)}%`}
                y={BASELINE_Y + 18}
                textAnchor="middle"
                className={isToday ? 'fill-ink font-semibold' : 'fill-ink-soft'}
                fontSize={11}
              >
                {isToday ? 'Today' : p.weekday}
              </text>
              <text
                x={`${bandCenter(i)}%`}
                y={BASELINE_Y + 32}
                textAnchor="middle"
                className="fill-ink-faint tabular-nums"
                fontSize={10}
              >
                {p.date}
              </text>
            </g>
          );
        })}
      </svg>

      {maxSpend === 0 && (
        <p className="mt-2 text-center text-sm text-ink-soft">Nothing spent yet this week.</p>
      )}

      {/* The same data, reachable by a screen reader or by anyone who can't read the marks. */}
      <table className="sr-only">
        <caption>Spending this week by day</caption>
        <thead>
          <tr>
            <th>Day</th>
            <th>Date</th>
            <th>Spent</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const p = dayParts(d.date);
            const over = dailyBudget != null && d.total > dailyBudget;
            return (
              <tr key={`row-${d.date}`}>
                <td>{p.weekday}</td>
                <td>{p.date}</td>
                <td>{money(d.total)}</td>
                <td>{dailyBudget == null ? 'No budget set' : over ? 'Over daily budget' : 'On track'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </figure>
  );
}
